/**
 * When to warn that the WhatsApp connection is about to die.
 *
 * Pure rule, no React, so it can be reasoned about and reused (banner, settings
 * card, later a digest email) without three copies drifting apart. Mirrors
 * lib/plan-alert.js on purpose.
 *
 * Only Embedded Signup tokens expire - Meta's Tech Provider template mints a
 * 60-day token and has no never-expiring variant. A BYO system-user token has no
 * expiry, passes expires_at = null, and correctly gets no alert.
 *
 * The thresholds are deliberately early. Reconnecting means a person signing in
 * to Facebook and stepping through Meta's popup, which is not a thing anyone does
 * within the hour, so a warning that first appears on the last day is useless.
 */
export function waTokenAlert(health) {
  const expiresAt = health?.expires_at;
  if (!expiresAt) return null;

  const days = typeof health.days_left === 'number'
    ? health.days_left
    : Math.floor((new Date(expiresAt).getTime() - Date.now()) / 86400000);
  if (Number.isNaN(days)) return null;

  if (days < 0) {
    return {
      code: 'expired',
      level: 'danger',
      days,
      title: 'WhatsApp is disconnected',
      // Say what actually stopped, not "token expired" - a tenant does not think
      // in tokens, they think in messages that are no longer going out.
      detail: 'Meta expired the connection, so recovery messages and replies have stopped. Reconnecting takes a minute.',
    };
  }
  if (days <= 3) {
    return {
      code: 'critical',
      level: 'danger',
      days,
      title: days === 0 ? 'WhatsApp disconnects today' : `WhatsApp disconnects in ${days} day${days === 1 ? '' : 's'}`,
      detail: 'Reconnect now to keep recovery messages sending.',
    };
  }
  if (days <= 14) {
    return {
      code: 'soon',
      level: 'warning',
      days,
      title: `WhatsApp disconnects in ${days} days`,
      detail: 'Meta expires this connection every 60 days. Reconnect to keep messages sending.',
    };
  }
  return null;
}

export default waTokenAlert;
